import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import type { LabClient } from '@alpha-edge/shared'
import { api } from '../../services/api'
import { colors } from '../../components/ui/styles'
import { FacilityEditModal, type FacilityEntry } from './FacilityEditModal'

interface EhrConfigData {
  readonly id?: string
  readonly ehrId: string
  readonly ehrName: string
  readonly version: string
  readonly enabled: boolean
  readonly configJson: Record<string, unknown>
}

type Tab = 'general' | 'facilities' | 'json'

const tabStyle = (active: boolean) => ({
  padding: '0.5rem 1rem',
  borderRadius: '6px 6px 0 0',
  border: `1px solid ${active ? colors.primary : colors.border}`,
  borderBottom: active ? `1px solid ${colors.bgCard}` : `1px solid ${colors.border}`,
  background: active ? colors.bgCard : colors.bgMuted,
  color: active ? colors.primary : colors.textMuted,
  cursor: 'pointer',
  fontWeight: active ? (600 as const) : (400 as const),
  fontSize: '0.875rem',
})

const inputStyle = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '0.875rem',
  boxSizing: 'border-box' as const,
}

const labelStyle = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: 500 as const,
  marginBottom: '0.25rem',
  color: '#374151',
}

const btnStyle = {
  padding: '0.5rem 1rem',
  borderRadius: '6px',
  border: 'none',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: 500 as const,
}

const emptyConfig: EhrConfigData = {
  ehrId: '',
  ehrName: '',
  version: '1.0.0',
  enabled: true,
  configJson: {
    transport: { type: 'sftp', host: '', port: 22, authType: 'password', username: '', credential: '' },
    hl7: { version: '2.5.1', encoding: '^~\\&', segmentOrder: ['MSH', 'PID', 'ORC', 'OBR', 'OBX'], segments: {} },
    operations: { sendResults: { enabled: true }, receiveOrders: { enabled: false } },
    codeMappings: { testCodes: {}, specimenTypes: {}, resultFlags: {} },
    facilities: [],
  },
}

export function EhrConfigPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isNew = id === 'new'

  const [config, setConfig] = useState<EhrConfigData>(emptyConfig)
  const [jsonText, setJsonText] = useState('')
  const [activeTab, setActiveTab] = useState<Tab>('general')
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)

  const [vaultKeys, setVaultKeys] = useState<readonly string[]>([])

  useEffect(() => {
    let cancelled = false
    api
      .get<readonly { secretKey: string }[]>('/tenant-secrets')
      .then((res) => {
        if (!cancelled) setVaultKeys((res.data ?? []).map((s) => s.secretKey))
      })
      .catch(() => {
        /* vault keys are optional; silent failure is fine */
      })
    return () => {
      cancelled = true
    }
  }, [])

  const [facilityModalOpen, setFacilityModalOpen] = useState(false)
  const [facilityEditIndex, setFacilityEditIndex] = useState<number | null>(null)
  const [labClients, setLabClients] = useState<readonly LabClient[]>([])

  const fetchConfig = useCallback(async () => {
    if (isNew) return
    try {
      setLoading(true)
      const result = await api.get<EhrConfigData>(`/ehr-configs/${id}`)
      if (result.data) {
        setConfig(result.data)
        setJsonText(JSON.stringify(result.data.configJson, null, 2))
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load config')
    } finally {
      setLoading(false)
    }
  }, [id, isNew])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  useEffect(() => {
    setJsonText(JSON.stringify(config.configJson, null, 2))
  }, [config.configJson])

  useEffect(() => {
    let cancelled = false
    api
      .get<readonly LabClient[]>('/lab-clients?limit=100')
      .then((res) => {
        if (!cancelled) setLabClients(res.data ?? [])
      })
      .catch(() => {
        /* lab-client lookup is cosmetic; silent failure is fine */
      })
    return () => {
      cancelled = true
    }
  }, [])

  const facilities = ((config.configJson.facilities as readonly FacilityEntry[] | undefined) ??
    []) as readonly FacilityEntry[]

  const setFacilities = (next: readonly FacilityEntry[]) => {
    setConfig((prev) => ({
      ...prev,
      configJson: { ...prev.configJson, facilities: next },
    }))
  }

  const handleFacilitySave = (entry: FacilityEntry) => {
    if (facilityEditIndex === null) {
      setFacilities([...facilities, entry])
    } else {
      setFacilities(facilities.map((f, i) => (i === facilityEditIndex ? entry : f)))
    }
  }

  const handleFacilityRemove = (index: number) => {
    setFacilities(facilities.filter((_, i) => i !== index))
  }

  const openFacilityNew = () => {
    setFacilityEditIndex(null)
    setFacilityModalOpen(true)
  }

  const openFacilityEdit = (index: number) => {
    setFacilityEditIndex(index)
    setFacilityModalOpen(true)
  }

  const labelForLabClient = (id?: string) => {
    if (!id) return null
    const c = labClients.find((lc) => lc.id === id)
    return c ? `${c.code} - ${c.name}` : id
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      let configJson = config.configJson
      if (activeTab === 'json') {
        try {
          configJson = JSON.parse(jsonText) as Record<string, unknown>
        } catch {
          setError('Invalid JSON in config editor')
          return
        }
      }

      const payload = {
        ehrId: config.ehrId,
        ehrName: config.ehrName,
        version: config.version,
        enabled: config.enabled,
        configJson,
      }

      if (isNew) {
        await api.post('/ehr-configs', payload)
        setSuccess('EHR config created successfully')
        setTimeout(() => navigate('/settings/ehr'), 1000)
      } else {
        await api.put(`/ehr-configs/${id}`, payload)
        setSuccess('EHR config updated successfully')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save config')
    } finally {
      setSaving(false)
    }
  }

  const handleTestConnection = async () => {
    if (!config.id) return
    try {
      setTesting(true)
      setTestResult(null)
      const result = await api.post<{ success: boolean; message: string }>(
        `/ehr-configs/${config.id}/test-connection`,
        {},
      )
      setTestResult(result.data ?? { success: false, message: 'No response' })
    } catch (err) {
      setTestResult({ success: false, message: err instanceof Error ? err.message : 'Test failed' })
    } finally {
      setTesting(false)
    }
  }

  if (loading) {
    return <div style={{ padding: '2rem', color: colors.textMuted }}>Loading...</div>
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>{isNew ? 'New EHR Configuration' : `Edit: ${config.ehrName}`}</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => navigate('/settings/ehr')}
            style={{ ...btnStyle, background: '#f1f5f9', color: '#374151' }}
          >
            Cancel
          </button>
          {!isNew && (
            <button
              onClick={handleTestConnection}
              disabled={testing}
              style={{ ...btnStyle, background: '#f1f5f9', color: '#374151', opacity: testing ? 0.6 : 1 }}
              data-testid="test-connection-btn"
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ ...btnStyle, background: colors.primary, color: '#fff', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: '0.75rem',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '6px',
            color: '#dc2626',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: '0.75rem',
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '6px',
            color: '#16a34a',
            marginBottom: '1rem',
          }}
        >
          {success}
        </div>
      )}
      {testResult && (
        <div
          data-testid="test-connection-result"
          style={{
            padding: '0.75rem',
            background: testResult.success ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${testResult.success ? '#bbf7d0' : '#fecaca'}`,
            borderRadius: '6px',
            color: testResult.success ? '#16a34a' : '#dc2626',
            marginBottom: '1rem',
          }}
        >
          {testResult.success ? 'Connection successful' : `Connection failed: ${testResult.message}`}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '-1px', position: 'relative' as const, zIndex: 1 }}>
        <button onClick={() => setActiveTab('general')} style={tabStyle(activeTab === 'general')}>
          General
        </button>
        <button onClick={() => setActiveTab('facilities')} style={tabStyle(activeTab === 'facilities')}>
          Facilities ({facilities.length})
        </button>
        <button onClick={() => setActiveTab('json')} style={tabStyle(activeTab === 'json')}>
          JSON Config
        </button>
      </div>

      <div
        style={{
          padding: '1.5rem',
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: '0 8px 8px 8px',
        }}
      >
        {activeTab === 'general' && (
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '1rem' }}>
            <div>
              <label style={labelStyle}>EHR ID</label>
              <input
                style={inputStyle}
                value={config.ehrId}
                onChange={(e) => setConfig({ ...config, ehrId: e.target.value })}
                placeholder="e.g., calredie"
                disabled={!isNew}
              />
            </div>
            <div>
              <label style={labelStyle}>EHR Name</label>
              <input
                style={inputStyle}
                value={config.ehrName}
                onChange={(e) => setConfig({ ...config, ehrName: e.target.value })}
                placeholder="e.g., CalREDIE"
              />
            </div>
            <div>
              <label style={labelStyle}>Version</label>
              <input
                style={inputStyle}
                value={config.version}
                onChange={(e) => setConfig({ ...config, version: e.target.value })}
              />
            </div>
            <div>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={config.enabled}
                  onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                />
                Enabled
              </label>
            </div>

            {!isNew &&
              (() => {
                const transport = config.configJson.transport as Record<string, unknown> | undefined
                const credential = transport?.credential as string | undefined
                const isVaultRef = credential?.startsWith('tenant_secret:') ?? false
                const vaultKeyName = isVaultRef ? credential!.slice('tenant_secret:'.length) : ''

                const setCredential = (val: string) => {
                  setConfig((prev) => ({
                    ...prev,
                    configJson: {
                      ...prev.configJson,
                      transport: { ...(prev.configJson.transport as Record<string, unknown>), credential: val },
                    },
                  }))
                }

                return (
                  <div
                    style={{
                      borderTop: `1px solid ${colors.border}`,
                      paddingTop: '1rem',
                      marginTop: '0.5rem',
                    }}
                  >
                    <label style={labelStyle}>Transport Credential</label>
                    {isVaultRef ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span
                          data-testid="vault-ref-badge"
                          style={{
                            display: 'inline-block',
                            background: '#dbeafe',
                            color: '#1d4ed8',
                            borderRadius: '4px',
                            padding: '4px 10px',
                            fontSize: '0.8125rem',
                            fontFamily: 'monospace',
                          }}
                        >
                          vault: {vaultKeyName}
                        </span>
                        <button
                          type="button"
                          onClick={() => setCredential('')}
                          style={{ ...btnStyle, background: '#f1f5f9', color: '#374151', fontSize: '0.75rem' }}
                        >
                          Clear
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <input
                          style={{ ...inputStyle, flex: 1 }}
                          value={credential ?? ''}
                          onChange={(e) => setCredential(e.target.value)}
                          placeholder="Direct value or select from vault"
                          data-testid="credential-input"
                        />
                        {vaultKeys.length > 0 && (
                          <select
                            style={{ ...inputStyle, width: 'auto' }}
                            value=""
                            onChange={(e) => {
                              if (e.target.value) setCredential(`tenant_secret:${e.target.value}`)
                            }}
                            data-testid="vault-dropdown"
                          >
                            <option value="">From vault...</option>
                            {vaultKeys.map((k) => (
                              <option key={k} value={k}>
                                {k}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}
                  </div>
                )
              })()}
          </div>
        )}

        {activeTab === 'facilities' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ color: colors.textMuted, fontSize: '0.8125rem' }}>
                Per-lab-client routing: overrides deep-merge on top of the EHR defaults at send/receive time.
              </div>
              <button
                type="button"
                onClick={openFacilityNew}
                style={{ ...btnStyle, background: colors.primary, color: '#fff' }}
              >
                Add Facility
              </button>
            </div>

            {facilities.length === 0 ? (
              <div
                style={{
                  padding: '1.25rem',
                  textAlign: 'center' as const,
                  color: colors.textMuted,
                  background: colors.bgMuted,
                  borderRadius: '6px',
                }}
              >
                No facilities configured. All lab clients use the EHR defaults.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left' as const, borderBottom: `1px solid ${colors.border}` }}>
                    <th style={{ padding: '0.5rem' }}>Lab Client</th>
                    <th style={{ padding: '0.5rem' }}>Sending Facility</th>
                    <th style={{ padding: '0.5rem' }}>Receiving Facility</th>
                    <th style={{ padding: '0.5rem' }}>Overrides</th>
                    <th style={{ padding: '0.5rem', width: '140px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {facilities.map((f, i) => {
                    const overrideKeys = f.overrides
                      ? Object.keys(f.overrides).filter((k) => f.overrides?.[k as keyof typeof f.overrides])
                      : []
                    return (
                      <tr key={i} style={{ borderBottom: `1px solid ${colors.border}` }}>
                        <td style={{ padding: '0.5rem' }}>
                          {labelForLabClient(f.labClientId) ?? <span style={{ color: colors.textMuted }}>--</span>}
                        </td>
                        <td style={{ padding: '0.5rem' }}>
                          {f.sendingFacility ?? <span style={{ color: colors.textMuted }}>--</span>}
                        </td>
                        <td style={{ padding: '0.5rem' }}>
                          {f.receivingFacility ?? <span style={{ color: colors.textMuted }}>--</span>}
                        </td>
                        <td style={{ padding: '0.5rem', color: colors.textMuted, fontSize: '0.8125rem' }}>
                          {overrideKeys.length === 0 ? '--' : overrideKeys.join(', ')}
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'right' as const }}>
                          <button
                            type="button"
                            onClick={() => openFacilityEdit(i)}
                            style={{ ...btnStyle, background: '#f1f5f9', color: '#374151', marginRight: '0.25rem' }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleFacilityRemove(i)}
                            style={{ ...btnStyle, background: '#fee2e2', color: '#dc2626' }}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'json' && (
          <div>
            <label style={labelStyle}>Full Configuration JSON</label>
            <textarea
              style={{
                ...inputStyle,
                fontFamily: 'monospace',
                fontSize: '0.8125rem',
                minHeight: '400px',
                resize: 'vertical' as const,
              }}
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
            />
          </div>
        )}
      </div>

      <FacilityEditModal
        isOpen={facilityModalOpen}
        isNew={facilityEditIndex === null}
        facility={facilityEditIndex === null ? null : (facilities[facilityEditIndex] ?? null)}
        onClose={() => setFacilityModalOpen(false)}
        onSave={handleFacilitySave}
      />
    </div>
  )
}
